import { useState, useEffect, useCallback, useRef } from "react";
import {
  getCurrentAppPosition,
  getGeolocationPermissionState,
  mapGeolocationError,
  requestGeolocationPermissions,
  subscribeToGeolocationPermission,
} from "@/lib/nativeGeolocation";

export type PermissionState = "prompt" | "granted" | "denied" | "unsupported" | "checking";

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  loading: boolean;
  error: string | null;
  permissionState: PermissionState;
}

export function useGeolocation(autoDetect = false) {
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    accuracy: null,
    loading: false,
    error: null,
    permissionState: "checking",
  });
  const watchingPermission = useRef(false);
  const hasAutoDetected = useRef(false);

  // Check and watch permission state via platform-aware geolocation API
  useEffect(() => {
    let mounted = true;

    void getGeolocationPermissionState().then((permissionState) => {
      if (!mounted) return;
      setState((s) => ({ ...s, permissionState }));
    });

    if (watchingPermission.current) {
      return () => {
        mounted = false;
      };
    }

    watchingPermission.current = true;
    const unsubscribe = subscribeToGeolocationPermission((permissionState) => {
      if (!mounted) return;
      setState((s) => ({ ...s, permissionState }));
    });

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, []);

  const requestLocation = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));

    void (async () => {
      const permissionState = await requestGeolocationPermissions();
      if (permissionState === "denied" || permissionState === "unsupported") {
        setState((s) => ({
          ...s,
          loading: false,
          error: permissionState === "denied" ? "permission_denied" : "Geolocation not supported",
          permissionState,
        }));
        return;
      }

      try {
        const position = await getCurrentAppPosition({ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
        setState((s) => ({
          ...s,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          loading: false,
          error: null,
          permissionState: "granted",
        }));
      } catch (error) {
        const mapped = mapGeolocationError(error);
        setState((s) => ({
          ...s,
          loading: false,
          error: mapped.message,
          permissionState: mapped.code === 1 ? "denied" : s.permissionState,
        }));
      }
    })();
  }, []);

  // Auto-detect: only request if permission is granted or prompt (not denied)
  useEffect(() => {
    if (!autoDetect || hasAutoDetected.current) return;
    if (state.permissionState === "checking") return; // wait for permission check

    if (state.permissionState === "granted" || state.permissionState === "prompt") {
      hasAutoDetected.current = true;
      requestLocation();
    } else if (state.permissionState === "denied") {
      hasAutoDetected.current = true;
      // Don't request – it will fail. Just set error state.
      setState((s) => ({ ...s, error: "permission_denied" }));
    }
  }, [autoDetect, state.permissionState, requestLocation]);

  return { ...state, requestLocation };
}

/** Calculate distance in km between two coordinates using Haversine formula */
export function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
